import { create } from 'zustand';
import { getFlagValue } from 'utils/getFlagValue/getFlagValue';

/**
 * PDFTron's own defaults, per the WebViewer docs. The panel starts from these so the numbers it
 * shows describe what the viewers are actually doing before anyone touches them.
 */
export const PDFTRON_STOCK_FIXED_RENDER_SETTINGS = {
  cachingLevel: 10,
  preRenderLevel: 5,
  progressiveTime: 3000,
} as const;

export const WEBVIEWER_RENDER_SETTINGS_FLAG = 'webviewerRenderSettings';

/** PDFTron's stock canvas multiplier is the browser's devicePixelRatio, so it can only be read at runtime. */
export const getStockCanvasMultiplier = (): number => window.devicePixelRatio || 1;

export const getStockRenderSettings = (): RenderSettings => ({
  ...PDFTRON_STOCK_FIXED_RENDER_SETTINGS,
  canvasMultiplier: getStockCanvasMultiplier(),
});

export type RenderSettings = {
  cachingLevel: number;
  preRenderLevel: number;
  progressiveTime: number;
  canvasMultiplier: number;
};

export type RenderSettingKey = keyof RenderSettings;

// Infinity is as unusable as NaN in PDFTron's setters, so require a finite number rather than
// merely a non-NaN one — a misconfigured flag should fall back to stock, not reach the engine.
const coerceNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/**
 * Shape of the `webviewerRenderSettings` JSON flag: any subset of the render settings.
 *
 * The `number` value types describe the contract we expect an operator to configure, not something
 * LaunchDarkly enforces — a JSON flag can hold any value. Every read below therefore still goes
 * through `coerceNumber`, which is what actually guarantees a finite number reaches PDFTron.
 */
export type RenderSettingsFlagValue = Partial<RenderSettings>;

/** Merge a partial LaunchDarkly JSON flag value with PDFTron stock defaults. */
export const resolveRenderSettingsFromFlag = (partial: unknown): RenderSettings => {
  const stock = getStockRenderSettings();

  if (!partial || typeof partial !== 'object' || Array.isArray(partial)) {
    return stock;
  }

  const record = partial as RenderSettingsFlagValue;

  return {
    cachingLevel: coerceNumber(record.cachingLevel, stock.cachingLevel),
    preRenderLevel: coerceNumber(record.preRenderLevel, stock.preRenderLevel),
    progressiveTime: coerceNumber(record.progressiveTime, stock.progressiveTime),
    canvasMultiplier: coerceNumber(record.canvasMultiplier, stock.canvasMultiplier),
  };
};

/**
 * The render settings for the current user: the LaunchDarkly flag merged over PDFTron stock.
 *
 * getFlagValue is typed for boolean flags and defaults to `false` when the flag is unset, which
 * resolveRenderSettingsFromFlag already treats as "no overrides" — so the JSON payload only needs
 * narrowing to unknown to keep LaunchDarkly's `any` out of the rest of the module.
 */
export const getRenderSettings = (): RenderSettings => {
  try {
    const flagValue: unknown = getFlagValue(WEBVIEWER_RENDER_SETTINGS_FLAG);
    return resolveRenderSettingsFromFlag(flagValue);
  } catch {
    return getStockRenderSettings();
  }
};

export const settingsEqual = (a: RenderSettings, b: RenderSettings): boolean =>
  a.cachingLevel === b.cachingLevel &&
  a.preRenderLevel === b.preRenderLevel &&
  a.progressiveTime === b.progressiveTime &&
  a.canvasMultiplier === b.canvasMultiplier;

// PDFTron sizes its rendered-canvas cache as 0.5 MB * cachingLevel^2 (524288 bytes * level^2).
// The panel exposes this as a memory budget in MB, so convert between the two representations.
const MB_PER_CACHING_LEVEL_SQUARED = 0.5;

export const cachingLevelToMb = (level: number): number => MB_PER_CACHING_LEVEL_SQUARED * level * level;

export const mbToCachingLevel = (mb: number): number => (mb > 0 ? Math.sqrt(mb / MB_PER_CACHING_LEVEL_SQUARED) : 0);

interface RenderSettingsStore extends RenderSettings {
  setSetting: (key: RenderSettingKey, value: number) => void;
  reset: () => void;
}

export const useRenderSettingsStore = create<RenderSettingsStore>((set) => ({
  ...getStockRenderSettings(),

  setSetting: (key, value) => set({ [key]: value }),

  reset: () => set({ ...getRenderSettings() }),
}));
